import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { Navigate, generatePath } from 'react-router-dom';
import { useLocalStorage } from 'react-use';

import { useTypedParams } from '@sorare/routing';

import {
  So5FixtureEvent,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { NBA_PLAY_CHASE_EVENTTYPE_FIXTURE_USERGROUP_USER } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { STORAGE } from '@sorare/core/src/hooks/useLocalStorage';
import { uniqueBy } from '@sorare/core/src/lib/arrays';
import { ScarcityType } from '@sorare/core/src/lib/scarcity';

import { getFixtureQueryVariables } from '@sorare/gameplay/src/lib/so5';

import type {
  NBAChasePageQuery,
  NBAChasePageQueryVariables,
  PlayChasePage_so5Fixture,
} from './__generated__/page.graphql';
import { PageParams } from './__generated__/routeParams';

const FIXTURE_FRAGMENT = gql`
  fragment PlayChasePage_so5Fixture on So5Fixture {
    slug
    gameWeek
    so5Leaderboards {
      slug
      displayName
      rarityType
      mainRarityType
      universalSo5UserGroups {
        slug
        displayName
        rarityType
        mainRarityType
      }
    }
  }
` as TypedDocumentNode<PlayChasePage_so5Fixture>;

const query = gql`
  query NBAChasePageQuery(
    $sport: Sport
    $eventType: So5FixtureEvent
    $type: So5State
    $slug: String
    $oldSo5FixtureSlug1: String
    $hasOldSo5Fixture1: Boolean!
    $oldSo5FixtureSlug2: String
    $hasOldSo5Fixture2: Boolean!
  ) {
    so5 {
      oldSo5Fixture1: so5Fixture(slug: $oldSo5FixtureSlug1)
        @include(if: $hasOldSo5Fixture1) {
        slug
        ...PlayChasePage_so5Fixture
      }
      oldSo5Fixture2: so5Fixture(slug: $oldSo5FixtureSlug2)
        @include(if: $hasOldSo5Fixture2) {
        slug
        ...PlayChasePage_so5Fixture
      }
      pastSo5Fixture: so5Fixture(type: PAST, sport: $sport) {
        slug
        ...PlayChasePage_so5Fixture
      }
      upcomingSo5Fixture: so5Fixture(
        type: UPCOMING
        sport: $sport
        eventType: $eventType
      ) {
        slug
        ...PlayChasePage_so5Fixture
      }
      so5Fixture(
        type: $type
        slug: $slug
        sport: $sport
        eventType: $eventType
      ) {
        slug
        ...PlayChasePage_so5Fixture
      }
    }
  }
  ${FIXTURE_FRAGMENT}
` as TypedDocumentNode<NBAChasePageQuery, NBAChasePageQueryVariables>;

const PlayChasePage = () => {
  const {
    flags: { useNbaChaseOldFixtures = [] },
  } = useFeatureFlags();
  const [sportLastVisitedChaseScarcity, setSportLastVisitedChaseScarcity] =
    useLocalStorage(
      STORAGE.sportLastVisitedChaseScarcity,
      {} as Record<Sport, ScarcityType>
    );
  const lastVisitedChaseScarcity =
    sportLastVisitedChaseScarcity?.[Sport.NBA] || 'common';

  const { fixture: fixtureSlug, eventType } = useTypedParams<PageParams>();
  const { currentUser } = useCurrentUserContext();

  const userSlug = currentUser?.slug || '';

  const { data } = useQuery(query, {
    variables: {
      ...getFixtureQueryVariables(fixtureSlug),
      eventType: eventType.toUpperCase() as So5FixtureEvent,
      sport: Sport.NBA,
      oldSo5FixtureSlug1: useNbaChaseOldFixtures[0],
      hasOldSo5Fixture1: useNbaChaseOldFixtures.length > 0,
      oldSo5FixtureSlug2: useNbaChaseOldFixtures[1],
      hasOldSo5Fixture2: useNbaChaseOldFixtures.length > 1,
    },
  });

  const chaseLeaderboards = uniqueBy(
    l => l.slug,
    [
      ...(data?.so5.upcomingSo5Fixture?.so5Leaderboards ?? []),
      ...(data?.so5.so5Fixture?.so5Leaderboards ?? []),
      ...(data?.so5.pastSo5Fixture?.so5Leaderboards ?? []),
      ...(data?.so5.oldSo5Fixture2?.so5Leaderboards ?? []),
      ...(data?.so5.oldSo5Fixture1?.so5Leaderboards ?? []),
    ].filter(l => l.universalSo5UserGroups.length > 0) ?? []
  );

  const userGroupSlug = chaseLeaderboards?.find(({ rarityType }) => {
    return rarityType === lastVisitedChaseScarcity;
  })?.universalSo5UserGroups[0]?.slug;

  if ((data && chaseLeaderboards.length === 0) || !userSlug) {
    return (
      <div>
        <FormattedMessage
          id="Play.chase.noChaseLeaderboardFound"
          defaultMessage="No chase leaderboard found"
        />
      </div>
    );
  }

  if (chaseLeaderboards.length > 0 && !userGroupSlug) {
    setSportLastVisitedChaseScarcity(
      prev =>
        ({
          ...prev,
          [Sport.NBA]: chaseLeaderboards[0]?.rarityType as ScarcityType,
        }) as Record<Sport, ScarcityType>
    );
  }

  // chase leaderboard found, redirect to Chase competition page
  if (userGroupSlug) {
    return (
      <Navigate
        to={generatePath(NBA_PLAY_CHASE_EVENTTYPE_FIXTURE_USERGROUP_USER, {
          fixture: fixtureSlug,
          userGroup: userGroupSlug,
          user: userSlug,
          eventType,
        })}
        replace
      />
    );
  }

  return null;
};

export default PlayChasePage;
